import { Injectable } from "@angular/core";
import { Observable, map } from "rxjs";
import { ApiService } from "../../_services/api-data.service";
import { URLS } from "../../_config/api.config";
import {
  PRICE_GROUP_EDITABLE_FIELDS,
  PriceGroupValues,
  PriceGroupRecord,
  PriceGroupResponse,
} from "./price-group.model";

interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
}
export interface PriceGroupsListing {
  total_count: number | string;
  result: PriceGroupResponse[];
  enable_next: boolean;
}

export function priceGroupRecord(row: PriceGroupResponse): PriceGroupRecord {
  return {
    ...row,
    id: row.price_group_id,
    createdAt: new Date(row.created_at),
  };
}

function editableValues(
  value: Partial<PriceGroupValues>,
): Partial<PriceGroupValues> {
  const fields: Partial<PriceGroupValues> = {};
  for (const key of PRICE_GROUP_EDITABLE_FIELDS) {
    if (value[key] !== undefined) Object.assign(fields, { [key]: value[key] });
  }
  return fields;
}

@Injectable({ providedIn: "root" })
export class PriceGroupsService {
  constructor(private api: ApiService) {}

  list(params: Record<string, string>): Observable<PriceGroupsListing> {
    return this.api
      .get(URLS.priceGroupsList, {
        ...params,
        dropdown: "false",
        isSupplierRequest: "true",
      })
      .pipe(
        map((response: ApiResponse<PriceGroupsListing>) =>
          this.unwrap(response),
        ),
      );
  }

  details(id: number): Observable<PriceGroupRecord> {
    return this.api
      .get(URLS.priceGroupDetails, { price_group_id: String(id) })
      .pipe(
        map((response: ApiResponse<PriceGroupResponse>) =>
          priceGroupRecord(this.unwrap(response)),
        ),
      );
  }

  create(value: PriceGroupValues): Observable<boolean> {
    return this.api
      .post(URLS.priceGroupCreate, editableValues(value))
      .pipe(map((response: ApiResponse<boolean>) => this.unwrap(response)));
  }

  currentSupplierId(): Observable<number | string | null> {
    return this.api
      .get(URLS.supplierDetails)
      .pipe(
        map(
          (response: ApiResponse<{ supplier_id: number | string }>) =>
            this.unwrap(response).supplier_id ?? null,
        ),
      );
  }

  update(id: number, changes: Partial<PriceGroupValues>): Observable<boolean> {
    // Ownership, provenance and approval are always derived by the backend.
    return this.api
      .put(URLS.priceGroupUpdate, {
        price_group_id: id,
        ...editableValues(changes),
      })
      .pipe(map((response: ApiResponse<boolean>) => this.unwrap(response)));
  }

  private unwrap<T>(response: ApiResponse<T>): T {
    if (Number(response.code) !== 200)
      throw new Error(response.message || "The price group request failed.");
    return response.data;
  }
}
